import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronDown, Loader2, Save, RotateCcw } from 'lucide-react';
import clsx from 'clsx';

import Button from '@components/common/Button';
import { CronExpressionBuilder } from '@components/common/CronExpressionBuilder';
import type { CronBuilderState } from '@components/common/CronExpressionBuilder';
import {
  buildCronExpression,
  createDefaultCronState,
  formatNextRun,
  getNextRunAt,
  humanizeCronExpression,
  isValidCronExpression,
} from '@components/common/CronExpressionBuilder/cronUtils';
import SettingsSectionHeader from '@components/features/data-management-settings/SettingsSectionHeader';
import { useReportDirty } from '@components/features/data-management-settings/useReportDirty';
import type { SettingsPaneProps } from '@components/features/data-management-settings/settingsNav';
import type { JobSchedule, UpdateJobScheduleRequest } from '@app-types/job-schedule';
import { jobScheduleService } from '@services';
import { getApiErrorMessage } from '@utils/apiError';
import { showToast } from '@utils/toast';
import styles from './JobSchedulesSettings.module.css';

interface DraftRow {
  cronExpression: string;
  isActive: boolean;
  builder: CronBuilderState;
}

function toDraft(job: JobSchedule): DraftRow {
  return {
    cronExpression: job.cronExpression,
    isActive: job.isActive,
    builder: createDefaultCronState(job.cronExpression),
  };
}

function isDirty(draft: DraftRow, job: JobSchedule): boolean {
  return draft.cronExpression !== job.cronExpression || draft.isActive !== job.isActive;
}

const JobSchedulesSettings: React.FC<SettingsPaneProps> = ({ onDirtyChange }) => {
  const [jobs, setJobs] = useState<JobSchedule[]>([]);
  const [drafts, setDrafts] = useState<Record<string, DraftRow>>({});
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [savingName, setSavingName] = useState<string | null>(null);
  const [expandedName, setExpandedName] = useState<string | null>(null);

  const loadJobs = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(false);
      const data = await jobScheduleService.getAll();
      setJobs(data);
      setDrafts(Object.fromEntries(data.map((job) => [job.jobName, toDraft(job)])));
    } catch {
      setLoadError(true);
      showToast.error('Failed to load job schedules');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadJobs();
  }, [loadJobs]);

  const paneDirty = useMemo(
    () => jobs.some((job) => {
      const draft = drafts[job.jobName];
      return draft ? isDirty(draft, job) : false;
    }),
    [drafts, jobs]
  );

  useReportDirty(paneDirty, onDirtyChange);

  const handleBuilderChange = (jobName: string, builder: CronBuilderState) => {
    setDrafts((prev) => ({
      ...prev,
      [jobName]: {
        ...prev[jobName],
        builder,
        cronExpression: buildCronExpression(builder),
      },
    }));
  };

  const handleToggleActive = (jobName: string) => {
    setDrafts((prev) => ({
      ...prev,
      [jobName]: { ...prev[jobName], isActive: !prev[jobName].isActive },
    }));
  };

  const handleReset = (job: JobSchedule) => {
    setDrafts((prev) => ({ ...prev, [job.jobName]: toDraft(job) }));
  };

  const handleSave = async (job: JobSchedule) => {
    const draft = drafts[job.jobName];
    if (!draft) return;

    if (!isValidCronExpression(draft.cronExpression)) {
      showToast.error('Enter a valid 5-field cron expression');
      return;
    }

    const input: UpdateJobScheduleRequest = {};
    if (draft.cronExpression !== job.cronExpression) input.cronExpression = draft.cronExpression;
    if (draft.isActive !== job.isActive) input.isActive = draft.isActive;

    try {
      setSavingName(job.jobName);
      const updated = await jobScheduleService.update(job.jobName, input);
      setJobs((prev) => prev.map((item) => (item.jobName === updated.jobName ? updated : item)));
      setDrafts((prev) => ({ ...prev, [updated.jobName]: toDraft(updated) }));
      showToast.success(`Job "${job.jobName}" saved`);
    } catch (error) {
      showToast.error(getApiErrorMessage(error, 'Failed to save job schedule'));
    } finally {
      setSavingName(null);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading} data-test-id="job-schedules-loading">
        <Loader2 size={48} className="spin" />
        <p>Loading scheduled jobs...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className={styles.error} data-test-id="job-schedules-error">
        Failed to load job schedules
      </div>
    );
  }

  return (
    <div className={styles.container} data-test-id="job-schedules-settings">
      <SettingsSectionHeader
        title="Scheduled Jobs"
        description="Choose how often each job runs. All schedules use UTC."
      />

      {jobs.length === 0 ? (
        <div className={styles.empty} data-test-id="job-schedules-empty">
          No job schedules are configured.
        </div>
      ) : (
        <div className={styles.jobList}>
          {jobs.map((job) => {
            const draft = drafts[job.jobName];
            if (!draft) return null;
            const dirty = isDirty(draft, job);
            const isSaving = savingName === job.jobName;
            const expanded = expandedName === job.jobName;
            const nextRun = getNextRunAt(draft.cronExpression);
            const cronValid = isValidCronExpression(draft.cronExpression);

            return (
              <div key={job.jobName} className={styles.jobCard} data-test-id={`job-schedule-row-${job.jobName}`}>
                <div className={styles.jobHeader}>
                  <div className={styles.jobInfo}>
                    <strong>{job.jobName}</strong>
                    <div className={styles.jobMeta}>
                      <span className={styles.badge}>{job.jobType}</span>
                      {job.containerName?.trim() ? (
                        <span
                          className={styles.containerBadge}
                          data-test-id={`job-schedule-container-${job.jobName}`}
                        >
                          Container: {job.containerName}
                        </span>
                      ) : null}
                    </div>
                    <p className={styles.scheduleSummary}>{humanizeCronExpression(draft.cronExpression)}</p>
                    {nextRun && (
                      <p className={styles.nextRun}>Next: {formatNextRun(nextRun)}</p>
                    )}
                  </div>
                  <div className={styles.jobToolbar}>
                    <label className={styles.toggle} data-test-id={`job-schedule-status-${job.jobName}`}>
                      <input
                        type="checkbox"
                        checked={draft.isActive}
                        onChange={() => handleToggleActive(job.jobName)}
                        className={styles.toggleInput}
                      />
                      <span className={styles.toggleSlider} />
                      <span className={styles.toggleLabel}>
                        {draft.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </label>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setExpandedName(expanded ? null : job.jobName)}
                      dataTestId={`job-schedule-edit-${job.jobName}`}
                    >
                      <ChevronDown size={14} className={clsx(expanded && styles.chevronOpen)} />
                      {expanded ? 'Hide' : 'Edit schedule'}
                    </Button>
                  </div>
                </div>

                {expanded && (
                  <div className={styles.jobEditor}>
                    <CronExpressionBuilder
                      idPrefix={`job-schedule-${job.jobName}`}
                      value={draft.builder}
                      onChange={(builder) => handleBuilderChange(job.jobName, builder)}
                    />
                    <div className={styles.actions}>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleReset(job)}
                        disabled={!dirty || isSaving}
                        dataTestId={`job-schedule-reset-${job.jobName}`}
                      >
                        <RotateCcw size={14} />
                        Reset
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void handleSave(job)}
                        disabled={!dirty || isSaving || !cronValid}
                        loading={isSaving}
                        loadingText="Saving..."
                        dataTestId={`job-schedule-save-${job.jobName}`}
                      >
                        <Save size={14} />
                        Save
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default JobSchedulesSettings;
